import { Info, Trash2 } from "lucide-react";
import type { JSX } from "react";

import type {
  CalculatorLevel,
  FormColumn as ServiceFormColumn,
} from "../../services/calculatorService";
import { cx } from "../../utils/functions";
import { Alert } from "../ui/Alert";
import type { ExtendedFormRow } from "./formTypes";
import {
  MIN_PROCUREMENT_ITEMS,
  PROCUREMENT_COLUMNS,
  PROCUREMENT_HEADER_LABELS,
  PROCUREMENT_TOOLTIPS,
} from "./procurementCatalogue";

/**
 * Procurement (Wet Lab Silver) table — issue #305.
 *
 * Presentational only. Autosave, validation, option resolution, collaboration
 * and submit all stay in Calculator and arrive here as `renderField`, so this
 * does not become a second, weaker copy of the matrix form engine.
 *
 * One add-row table: each row names a consumable through three cascading
 * dropdowns, then captures order quantities and a reuse option.
 */

/**
 * Twelfths. The eight columns plus Remove must total 12, or `table-fixed`
 * scales everything down until labels break mid-word.
 *
 * The three dropdowns carry the long option text ("Extra-Large Petri dish",
 * "Yes - recycled content") and get the width; the numeric fields need room for
 * four digits and no more.
 */
const COLUMN_WIDTH: Record<string, string> = {
  [PROCUREMENT_COLUMNS.itemCategory]: "w-2/12",
  [PROCUREMENT_COLUMNS.itemType]: "w-2/12",
  [PROCUREMENT_COLUMNS.sizeVolume]: "w-1/12",
  [PROCUREMENT_COLUMNS.catalogueNumber]: "w-1/12",
  [PROCUREMENT_COLUMNS.unitsPerPack]: "w-1/12",
  [PROCUREMENT_COLUMNS.packsPerOrder]: "w-1/12",
  [PROCUREMENT_COLUMNS.monthsBetweenOrders]: "w-1/12",
  [PROCUREMENT_COLUMNS.reuseOrAlternatives]: "w-2/12",
};

const columnTip = (column: ServiceFormColumn): string =>
  column.description || PROCUREMENT_TOOLTIPS[column.label] || "";

const headingClass = (level: CalculatorLevel): string =>
  level === "silver" ? "text-silver-heading" : "text-base-content";

const bodyClass = (level: CalculatorLevel): string =>
  level === "silver" ? "text-silver-body" : "text-base-content/70";

interface TableProps {
  level: CalculatorLevel;
  columns: ServiceFormColumn[];
  rows: ExtendedFormRow[];
  renderField: (row: ExtendedFormRow, column: ServiceFormColumn) => JSX.Element;
  /** Baseline lets rows be added and removed; savings inherits the baseline's
   * item set and must not change it. */
  allowRemoveRows: boolean;
  onRemoveRow: (rowId: string) => void;
  locked: boolean;
  /** Rows with every required field filled. Drives the progress line; the
   * submit gate itself lives in Calculator. */
  completeCount?: number;
  /** Savings has no minimum: the baseline already enforced it. */
  showItemCount?: boolean;
  /** A row's current values by column id, for building row headings. */
  valuesOf: (rowId: string) => Record<string, string>;
}

/**
 * Heading for a row in the stacked layout, built from whichever identity
 * columns the user has filled so far. An unnamed row still needs a heading, so
 * it falls back to its generated label.
 */
const rowHeadingFor = (
  row: ExtendedFormRow,
  columns: ServiceFormColumn[],
  valuesOf: (rowId: string) => Record<string, string>,
): string => {
  const values = valuesOf(String(row.id));
  const parts = [
    PROCUREMENT_COLUMNS.itemType,
    PROCUREMENT_COLUMNS.sizeVolume,
  ]
    .map((label) => columns.find((c) => c.label === label))
    .map((column) => (column ? values[String(column.id)] : ""))
    .filter((value) => value && value.trim() !== "");

  return parts.length > 0 ? parts.join(" · ") : row.label;
};

export const ProcurementTable = ({
  level,
  columns,
  rows,
  renderField,
  allowRemoveRows,
  onRemoveRow,
  locked,
  completeCount = 0,
  showItemCount = true,
  valuesOf,
}: TableProps): JSX.Element => {
  if (rows.length === 0) {
    return (
      <Alert variant="info" soft>
        <span>
          No items yet. Use <strong>Add Row</strong> below to record each
          single-use item your lab orders.
        </span>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* The table appears only at xl. Eight dense columns need roughly 1200px
          before labels start breaking mid-word, and scrolling that grid
          sideways is not a tablet layout — below xl the stacked cards are the
          real answer, not a fallback. */}
      <div className="hidden xl:block">
        <table className="table table-fixed table-sm table-cells-fit w-full">
          <thead>
            <tr>
              {columns.map((column, index) => {
                const tip = columnTip(column);
                // The last columns anchor their tooltip to the left, or a
                // 20rem-wide tip centred under a right-edge header spills past
                // the page. Same rule the Lab Waste table already applies.
                const isLastColumns = index >= columns.length - 2;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    // whitespace-normal overrides daisyui's `thead { white-space:
                    // nowrap }`. Under table-fixed a header that cannot wrap
                    // overflows its column and paints over the next one.
                    //
                    // Deliberately NOT overflow-hidden. That was here as a guard
                    // against an over-long header spilling into its neighbour,
                    // but the tooltip is anchored to the button inside this cell
                    // and clipping the cell clips the tooltip with it. The
                    // geometry check already asserts no clipping, no overlap and
                    // no mid-word break, so the guard cost a feature and bought
                    // nothing.
                    className={cx(
                      "thead-silver whitespace-normal p-0 align-bottom",
                      COLUMN_WIDTH[column.label] ?? "w-1/12",
                    )}
                  >
                    {/* A button, not a bare span: daisyui opens the tooltip on
                        :has(:focus-visible), so the header text is reachable by
                        keyboard instead of being hover-only information.
                        Block, not flex: the icon flows inline after the last
                        word instead of reserving a fixed slot beside the text.
                        In a ~90px column that slot was the difference between a
                        label fitting and breaking mid-word. */}
                    <button
                      type="button"
                      className={cx(
                        "tooltip block min-h-11 w-full cursor-help px-2 py-2 text-left text-xs font-semibold leading-tight",
                        isLastColumns ? "tooltip-left" : "tooltip-bottom",
                      )}
                      data-tip={tip}
                      // The compact header is display text; assistive tech gets
                      // the full column name plus the description.
                      aria-label={tip ? `${column.label}. ${tip}` : column.label}
                    >
                      <span>
                        {PROCUREMENT_HEADER_LABELS[column.label] ?? column.label}
                        {column.is_required && column.is_editable && (
                          <span className="text-error" aria-hidden="true">
                            {" *"}
                          </span>
                        )}
                      </span>
                      {tip && (
                        <Info
                          className="ml-1 inline h-3 w-3 align-text-bottom"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </th>
                );
              })}
              {allowRemoveRows && (
                <th
                  scope="col"
                  className="thead-silver w-1/12 whitespace-normal px-2 py-2 text-xs"
                >
                  Remove
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td key={`${row.id}-${column.id}`} className="p-1 align-middle">
                    {renderField(row, column)}
                  </td>
                ))}
                {allowRemoveRows && (
                  <td className="p-1 align-middle">
                    <button
                      type="button"
                      disabled={locked || rows.length <= 1}
                      className="btn btn-ghost btn-sm text-error"
                      onClick={() => onRemoveRow(String(row.id))}
                      aria-label={`Remove ${rowHeadingFor(row, columns, valuesOf)}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* One card per row, everywhere below xl. Column tips become visible text
          here rather than hover-only, since touch has no hover. */}
      <ul className="space-y-4 xl:hidden">
        {rows.map((row) => (
          <li
            key={row.id}
            className={cx(
              "rounded-box border-2 p-4",
              level === "silver" ? "card-silver" : "border-base-300 bg-base-100",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className={cx("text-sm font-semibold", headingClass(level))}>
                {rowHeadingFor(row, columns, valuesOf)}
              </h4>
              {allowRemoveRows && (
                <button
                  type="button"
                  disabled={locked || rows.length <= 1}
                  className="btn btn-ghost btn-sm min-h-11 text-error"
                  onClick={() => onRemoveRow(String(row.id))}
                  aria-label={`Remove ${rowHeadingFor(row, columns, valuesOf)}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <dl className="mt-3 space-y-4">
              {columns.map((column) => (
                <div key={`${row.id}-${column.id}-stacked`}>
                  <dt className={cx("text-xs font-semibold", headingClass(level))}>
                    {column.label}
                    {column.is_required && column.is_editable && (
                      <span className="text-error" aria-hidden="true">
                        {" *"}
                      </span>
                    )}
                  </dt>
                  {columnTip(column) && (
                    <p className={cx("mt-1 text-xs", bodyClass(level))}>
                      {columnTip(column)}
                    </p>
                  )}
                  <dd className="mt-2">{renderField(row, column)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>

      {showItemCount && (
        <p className={cx("text-xs", bodyClass(level))}>
          {completeCount} of {MIN_PROCUREMENT_ITEMS} items complete. Each item can
          only be added once, and you can mix categories freely.
        </p>
      )}
    </div>
  );
};
